import { Box, Stack, Typography } from '@mui/material';
import { useTheme, type Theme } from '@mui/material/styles';

import { radius } from '@karehero/llama/theme/radius';

import {
  Caption,
  ComponentSet,
  Frame,
  FrameGrid,
  LibraryShell,
} from '@/designs-dev/library/LibraryCanvas';
import { entry } from '@/designs-dev/library/library';

// The tokens everything else is built from, read live from the theme Loft
// renders with, so the page cannot drift from what the screens use.

const isColour = (v: unknown): v is string =>
  typeof v === 'string' && /^(#|rgb)/.test(v);

/** One colour: its fill, its path to reference it by, and its value. */
const Swatch = ({ path, value }: { path: string; value: string }) => (
  <Stack spacing={0.5} sx={{ width: 96 }}>
    <Box
      sx={{
        height: 56,
        borderRadius: `${radius.sm}px`,
        border: 1,
        borderColor: 'divider',
        bgcolor: value,
      }}
    />
    {/* A ramp's shade is enough under its swatch, with the ramp named above
        it; a role or a single colour has no ramp, so it gets its whole path. */}
    <Typography variant='mono' sx={{ wordBreak: 'break-word' }}>
      {path.startsWith('custom.') && path.split('.').length === 3
        ? path.split('.')[2]
        : path}
    </Typography>
    <Typography variant='mono' sx={{ color: 'text.secondary' }}>
      {value}
    </Typography>
  </Stack>
);

const Ramp = ({
  name,
  swatches,
}: {
  name: string;
  swatches: { path: string; value: string }[];
}) => (
  <Stack spacing={1}>
    <Typography variant='h6'>{name}</Typography>
    <Stack direction='row' spacing={1.5}>
      {swatches.map((s) => (
        <Swatch key={s.path} {...s} />
      ))}
    </Stack>
  </Stack>
);

/** The brand ramps under `custom`, then the roles the theme gives MUI. */
const colourRamps = (theme: Theme) => {
  const ramps: { name: string; swatches: { path: string; value: string }[] }[] =
    [];
  const single: { path: string; value: string }[] = [];
  for (const [key, val] of Object.entries(
    theme.palette.custom as unknown as Record<string, unknown>,
  )) {
    if (isColour(val)) single.push({ path: `custom.${key}`, value: val });
    else if (val && typeof val === 'object') {
      const swatches = Object.entries(val as Record<string, unknown>)
        .filter(([, v]) => isColour(v))
        .map(([shade, v]) => ({
          path: `custom.${key}.${shade}`,
          value: v as string,
        }));
      // A family collapsed to one colour keeps its ramp shape so old call
      // sites still resolve; draw it once, not as ten matching swatches.
      if (new Set(swatches.map((s) => s.value)).size === 1)
        single.push({ path: `custom.${key}`, value: swatches[0].value });
      else ramps.push({ name: key, swatches });
    }
  }
  if (single.length) ramps.push({ name: 'Single colours', swatches: single });
  const p = theme.palette;
  ramps.push({
    name: 'Roles',
    swatches: [
      { path: 'primary.main', value: p.primary.main },
      { path: 'secondary.main', value: p.secondary.main },
      { path: 'text.primary', value: p.text.primary },
      { path: 'text.secondary', value: p.text.secondary },
      { path: 'background.default', value: p.background.default },
      { path: 'background.paper', value: p.background.paper },
      { path: 'divider', value: p.divider },
    ],
  });
  return ramps;
};

/** The typography variants Figma has, in the order it lists them. */
const VARIANT_ORDER = [
  'display1',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'subtitle1',
  'subtitle2',
  'body1',
  'body2',
  'button',
  'caption',
  'overline',
];

const typeVariants = (theme: Theme) => {
  const typo = theme.typography as unknown as Record<
    string,
    Record<string, unknown>
  >;
  return Object.entries(typo)
    .filter(
      ([key, v]) =>
        key !== 'allVariants' && v && typeof v === 'object' && 'fontSize' in v,
    )
    .map(([key, v]) => ({
      name: key,
      spec: [
        // Figma sizes text in pixels, so read rem back at the 16px root.
        typeof v.fontSize === 'string' && v.fontSize.endsWith('rem')
          ? `${parseFloat(v.fontSize) * 16}px`
          : v.fontSize,
        v.fontWeight,
        v.lineHeight,
        String(v.fontFamily ?? '')
          .split(',')[0]
          .replace(/["']/g, ''),
      ]
        .filter((x) => x !== undefined && x !== '')
        .join(' · '),
    }))
    .sort((a, b) => {
      const ia = VARIANT_ORDER.indexOf(a.name);
      const ib = VARIANT_ORDER.indexOf(b.name);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
};

const SPACING_STEPS = [0.5, 1, 1.5, 2, 3, 4, 5, 6, 8, 10];

/** Breakpoints are drawn at a quarter of their width, so 1536 fits. */
const BREAKPOINT_SCALE = 0.25;

const FoundationsCanvas = () => {
  const theme = useTheme();
  const variants = typeVariants(theme);
  const figma = variants.filter((v) => VARIANT_ORDER.includes(v.name));
  const other = variants.filter((v) => !VARIANT_ORDER.includes(v.name));
  return (
    <LibraryShell title='Foundations'>
      <FrameGrid>
        <Frame title='Colours'>
          {colourRamps(theme).map((r) => (
            <Ramp key={r.name} {...r} />
          ))}
        </Frame>

        <Stack spacing={6}>
          {/* The variants Figma has, then the rest the theme carries: llama's
              older names, which nothing in Figma matches. */}
          {[
            { title: 'Typography', variants: figma },
            { title: 'Other type variants', variants: other },
          ].map((block) => (
            <Frame key={block.title} title={block.title}>
              {block.variants.map((v) => (
                <Stack key={v.name} spacing={0.5}>
                  <Typography variant={v.name as never}>{v.name}</Typography>
                  <Typography variant='mono' sx={{ color: 'text.secondary' }}>
                    {v.spec}
                  </Typography>
                </Stack>
              ))}
            </Frame>
          ))}
        </Stack>

        <Stack spacing={6}>
          <Frame title='Spacing'>
            <Stack spacing={1.5}>
              {SPACING_STEPS.map((n) => (
                <Stack
                  key={n}
                  direction='row'
                  spacing={2}
                  sx={{ alignItems: 'center' }}
                >
                  <Box sx={{ width: 120 }}>
                    <Typography variant='mono'>
                      {`${n} · ${theme.spacing(n)}`}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: theme.spacing(n),
                      height: 16,
                      bgcolor: 'primary.main',
                    }}
                  />
                </Stack>
              ))}
            </Stack>
          </Frame>

          <Frame title='Radius'>
            <Stack direction='row' spacing={3}>
              {Object.entries(radius).map(([name, r]) => (
                <Stack key={name} spacing={1} sx={{ alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      border: 1,
                      borderColor: 'primary.main',
                      bgcolor: 'custom.marineGreen.50',
                      borderRadius: `${Math.min(r, 36)}px`,
                    }}
                  />
                  <Caption>{`${name} · ${r}px`}</Caption>
                </Stack>
              ))}
            </Stack>
          </Frame>

          <Frame title='Breakpoints'>
            <Stack spacing={1.5}>
              {Object.entries(theme.breakpoints.values).map(([name, px]) => (
                <Stack
                  key={name}
                  direction='row'
                  spacing={2}
                  sx={{ alignItems: 'center' }}
                >
                  <Box sx={{ width: 120 }}>
                    <Typography variant='mono'>{`${name} · ${px}px`}</Typography>
                  </Box>
                  <Box
                    sx={{
                      width: Math.max(px * BREAKPOINT_SCALE, 2),
                      height: 16,
                      bgcolor: 'primary.main',
                    }}
                  />
                </Stack>
              ))}
            </Stack>
          </Frame>

          <Frame title='Icons'>
            <ComponentSet entry={entry('Icon')} />
          </Frame>
        </Stack>
      </FrameGrid>
    </LibraryShell>
  );
};

export default FoundationsCanvas;
